import React from 'react';
import { Autocomplete, Box, TextField, Tooltip } from '@mui/material';
import { Controller, useWatch } from 'react-hook-form';
import { get } from 'lodash';
import { useSelector } from 'react-redux';
import { useTranslatedMessages } from '../../hooks/useTranslatedMessages';

const TaxCategorySelect = ({
  id,
  name,
  label,
  control,
  errors,
  disabled = false,
  readOnly = false,
  onChange,
  getOptionDisabledReason,
}) => {
  const { common } = useTranslatedMessages();
  const taxCategorySelect = common.tax_category_select ?? {};
  const resolvedLabel = label ?? taxCategorySelect.label;
  const taxCategoryOptions = useSelector((state) => state.codeLists.taxCategoriesList) ?? [];
  // Use useWatch to get the current form value, which stays in sync with setValue calls
  const currentValue = useWatch({ control, name });

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        // Handle both string (category code) and object (full tax category object) cases
        // Use currentValue from useWatch to ensure we have the latest value (stays in sync with setValue calls)
        const fieldValue = typeof currentValue === 'string' ? currentValue : currentValue?.category;
        const selectedOption = taxCategoryOptions.find((opt) => opt.category === fieldValue) ?? null;

        return (
          <Autocomplete
            id={id || name}
            options={taxCategoryOptions}
            readOnly={readOnly}
            fullWidth={true}
            getOptionLabel={(option) => `${option.category} - ${option.description}`}
            getOptionDisabled={(option) => Boolean(getOptionDisabledReason?.(option))}
            isOptionEqualToValue={(option, value) => option.category === value?.category}
            value={selectedOption}
            renderOption={(props, option) => {
              const { key, onClick, onMouseDown, ...optionProps } = props;
              const disabledReason = getOptionDisabledReason?.(option);
              const optionLabel = `${option.category} - ${option.description}`;
              const blockDisabledSelection = (event) => {
                event.preventDefault();
                event.stopPropagation();
              };

              return (
                <Box
                  key={key}
                  component="li"
                  {...optionProps}
                  onMouseDown={disabledReason ? blockDisabledSelection : onMouseDown}
                  onClick={disabledReason ? blockDisabledSelection : onClick}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    opacity: disabledReason ? 0.5 : 1,
                    cursor: disabledReason ? 'not-allowed' : 'pointer',
                  }}
                >
                  {disabledReason ? (
                    <Tooltip title={disabledReason} placement="right">
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          maxWidth: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          pointerEvents: 'auto',
                        }}
                      >
                        {optionLabel}
                      </Box>
                    </Tooltip>
                  ) : (
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {optionLabel}
                    </Box>
                  )}
                </Box>
              );
            }}
            onChange={(_, selected) => {
              const disabledReason = getOptionDisabledReason?.(selected);
              if (disabledReason) {
                return;
              }

              // If there's an external onChange handler, let it manage the form value via setValue
              // useWatch will pick up the change and update the component
              if (onChange) {
                onChange(selected ?? null); // external handler will call setValue
              } else {
                // No external handler, update the Controller's field directly
                field.onChange(selected ?? null);
              }
            }}
            onBlur={field.onBlur}
            name={field.name}
            renderInput={(params) => {
              const errorObj = get(errors, name);
              return (
                <TextField
                  {...params}
                  label={resolvedLabel}
                  fullWidth={true}
                  error={!!errorObj}
                  helperText={errorObj?.message}
                />
              );
            }}
          />
        );
      }}
      disabled={disabled}
    />
  );
};

export default TaxCategorySelect;
